/**
 * What a past broadcast's rows say, counted per channel.
 *
 * History rows are one per (guest, channel) since SMS shipped, so every figure
 * on a history card has to either name a channel or say which grain it counts.
 * "3 failed" printed under "42 email · 18 SMS" is the blended number this whole
 * change exists to remove: the host cannot tell whether any text failed, and the
 * per-channel figure was on the payload all along.
 */
import {
  CHANNEL_ORDER,
  isChannel,
  shortChannelLabel,
  type BroadcastChannel,
} from './broadcastChannels';
import type { GroupBroadcastRecipient, GroupBroadcastSummary } from '../types/groups';

function clean(value: string | null | undefined): string {
  return String(value ?? '').trim().toLowerCase();
}

function count(value: number | null | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/** Rows of one status, split by the channel they were sent on. */
function tallyRows(
  recipients: readonly GroupBroadcastRecipient[] | null | undefined,
  status: string,
): Map<BroadcastChannel, number> {
  const byChannel = new Map<BroadcastChannel, number>();
  for (const recipient of recipients ?? []) {
    if (clean(recipient.status) !== status) continue;
    const channel = clean(recipient.channel);
    if (!isChannel(channel)) continue;
    byChannel.set(channel, (byChannel.get(channel) ?? 0) + 1);
  }
  return byChannel;
}

/** "2 email failed · 1 SMS failed", in canonical order, or null when there are none. */
function joinByChannel(byChannel: Map<BroadcastChannel, number>, suffix: string): string | null {
  const parts = CHANNEL_ORDER
    .filter((channel) => (byChannel.get(channel) ?? 0) > 0)
    .map((channel) => `${byChannel.get(channel)} ${shortChannelLabel(channel)} ${suffix}`);
  return parts.length > 0 ? parts.join(' · ') : null;
}

/**
 * Guests in a broadcast's history, counted once.
 *
 * History rows are one per (guest, channel) now, so an email+SMS send writes two
 * rows for the same person. "Who it went to (82)" reading 82 for a 41-guest send is
 * the row-grain change leaking into the copy.
 */
export function distinctRecipientCount(
  // Typed off the wire row rather than a hand-listed subset of it. The subset
  // spelled the identity fields out and so rejected an actual recipient — the
  // channel field it had no opinion on is the one every history row now carries.
  recipients: readonly Partial<GroupBroadcastRecipient>[],
): number {
  const keys = new Set(
    recipients.map(
      (recipient) =>
        recipient.inviteeId
        || recipient.email
        || recipient.phone
        || recipient.guestName
        || Math.random().toString(36),
    ),
  );
  return keys.size;
}

/** "Sent 118 email · 41 SMS" for a history card, falling back as the payload allows. */
export function historySentLine(
  broadcast: Pick<GroupBroadcastSummary, 'channelPlans' | 'recipients' | 'sentCount'>,
): string {
  const plans = (broadcast.channelPlans ?? []).filter((plan) => isChannel(clean(plan.channel)));
  if (plans.length > 0) {
    return `Sent ${plans
      .map((plan) => `${count(plan.sent)} ${shortChannelLabel(plan.channel)}`)
      .join(' · ')}`;
  }
  const byChannel = tallyRows(broadcast.recipients, 'sent');
  if (byChannel.size > 0) {
    return `Sent ${CHANNEL_ORDER.filter((channel) => byChannel.has(channel))
      .map((channel) => `${byChannel.get(channel)} ${shortChannelLabel(channel)}`)
      .join(' · ')}`;
  }
  return `Sent ${count(broadcast.sentCount)}`;
}

/**
 * "2 email failed · 1 SMS failed" — a failure count that names its channel.
 *
 * The stored plans carry `failed` per channel, so the blended count that used to
 * sit under the per-channel sent line was never the only number available. Null
 * when nothing failed, so the card says nothing rather than "0 failed".
 */
export function historyFailureLine(
  broadcast: Pick<GroupBroadcastSummary, 'channelPlans' | 'recipients'>,
): string | null {
  const plans = (broadcast.channelPlans ?? []).filter((plan) => isChannel(clean(plan.channel)));
  const byChannel = new Map<BroadcastChannel, number>();
  if (plans.length > 0) {
    for (const plan of plans) {
      const channel = clean(plan.channel) as BroadcastChannel;
      byChannel.set(channel, (byChannel.get(channel) ?? 0) + count(plan.failed));
    }
  } else {
    for (const [channel, failures] of tallyRows(broadcast.recipients, 'failed')) {
      byChannel.set(channel, failures);
    }
  }
  const perChannel = joinByChannel(byChannel, 'failed');
  if (perChannel) return perChannel;

  // A broadcast sent before the channel column existed recorded no channel on
  // its rows. It was email-only by definition, so an unqualified number here
  // cannot be mistaken for a text that failed.
  const unattributed = (broadcast.recipients ?? [])
    .filter((recipient) => clean(recipient.status) === 'failed').length;
  return unattributed > 0 ? `${unattributed} failed` : null;
}

/**
 * "19 email skipped · 96 SMS skipped" — who a past broadcast did not reach.
 *
 * The flat `skippedCount` is a per-guest total: it counts everyone the send
 * reached on no channel at all, which is not what "skipped 20" reads as beside
 * per-channel figures. Per-channel rows answer it properly; when there are none,
 * the fallback names the grain out loud instead of leaving it to be guessed.
 */
export function historySkippedLine(
  broadcast: Pick<GroupBroadcastSummary, 'recipients' | 'skippedCount'>,
): string | null {
  const perChannel = joinByChannel(tallyRows(broadcast.recipients, 'skipped'), 'skipped');
  if (perChannel) return perChannel;
  const guests = count(broadcast.skippedCount);
  return guests > 0 ? `${guests} guest${guests === 1 ? '' : 's'} got nothing` : null;
}
